import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { dataApi } from '@/lib/api';
import { ArrowLeft, Globe, Linkedin, Building, Users, DollarSign, MapPin, Activity, Mail } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

function formatNum(n) {
  if (!n) return '0';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return n.toLocaleString();
}

const chartTooltipStyle = { background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: 12 };

export default function AccountIntelligence() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartView, setChartView] = useState('department');

  useEffect(() => {
    setLoading(true);
    dataApi.getAccountDetail(decodeURIComponent(id))
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="space-y-6"><div className="h-20 skeleton-shimmer rounded-lg" /><div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-40 skeleton-shimmer rounded-lg" />)}</div></div>;
  if (!data) return <div className="text-center py-20 text-neutral-400">Account not found</div>;

  const { company, score, topics, by_department, by_seniority, by_content_type, geo_markers, recommended_contact, recent_engagements } = data;
  const chartData = chartView === 'department' ? by_department : chartView === 'seniority' ? by_seniority : by_content_type;
  const tierColor = score?.tier === 'Hot' ? 'bg-[#F66922] text-white' : score?.tier === 'Warm' ? 'bg-orange-100 text-orange-700' : 'bg-neutral-100 text-neutral-600';

  return (
    <div className="space-y-6" data-testid="account-intelligence-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Link to="/priority-accounts" className="mt-1 p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-300">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-[#0A0A0A] tracking-tight flex items-center gap-3">
            {company.CompanyName}
            {score && (
              <span className={`text-[11px] font-bold uppercase px-2.5 py-1 rounded-full ${tierColor}`}>
                {score.total_score} / {score.tier}
              </span>
            )}
          </h1>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {company.website && company.website !== '-' && (
              <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-1 text-xs text-[#F66922] hover:underline">
                <Globe className="w-3 h-3" />{company.website}
              </a>
            )}
            {company.linkedin && company.linkedin !== '-' && (
              <a href={company.linkedin} target="_blank" rel="noreferrer" className="text-[#F66922]"><Linkedin className="w-4 h-4" /></a>
            )}
            <span className="bg-neutral-100 text-neutral-500 text-[10px] font-medium px-2 py-0.5 rounded">{company.industry}</span>
            <span className="flex items-center gap-1 text-xs text-neutral-400"><Users className="w-3 h-3" />{company.headcount}</span>
            <span className="flex items-center gap-1 text-xs text-neutral-400"><DollarSign className="w-3 h-3" />{company.revenue}</span>
            <span className="flex items-center gap-1 text-xs text-neutral-400"><MapPin className="w-3 h-3" />{company.city}, {company.country}</span>
          </div>
        </div>
      </div>

      {/* Four Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Score Breakdown */}
        <div className="oaa-card p-4" data-testid="activity-card">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-[10px] font-bold text-neutral-400 uppercase tracking-[0.1em]">Account Score</h4>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${tierColor}`}>{score?.total_score || 0}</span>
          </div>
          {score && (
            <div className="space-y-2">
              {[
                { label: 'Signals', val: score.signals },
                { label: 'Contacts', val: score.contacts },
                { label: 'Departments', val: score.departments },
                { label: 'Senior Roles', val: score.senior_roles },
                { label: 'Content Depth', val: score.content_depth },
                { label: 'Qualified', val: score.qualified },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <span className="text-[11px] text-neutral-500 w-20">{item.label}</span>
                  <div className="flex-1 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#F66922] rounded-full" style={{ width: `${Math.min((item.val?.contribution || 0) / 50 * 100, 100)}%` }} />
                  </div>
                  <span className="text-[10px] tabular-nums text-neutral-400 w-6 text-right">{item.val?.value || 0}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Most Researched Topics */}
        <div className="oaa-card p-4" data-testid="topics-card">
          <h4 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-3">Most Researched Topics</h4>
          <ul className="space-y-1.5">
            {topics.map((t, i) => (
              <li key={i} className="text-xs text-neutral-500 flex items-start gap-2">
                <span className="w-4 h-4 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                <span className="truncate" title={t.topic}>{t.topic}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Business Need */}
        <div className="oaa-card p-4" data-testid="business-need-card">
          <h4 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-3">Business Need</h4>
          <p className="text-xs text-neutral-500 leading-relaxed">
            Based on engagement signals, {company.CompanyName} is actively researching {topics.length > 0 ? topics.slice(0, 3).map(t => t.topic).join(', ') : 'various technologies'} to optimize operations, improve efficiency, and drive digital transformation initiatives across their organization.
          </p>
        </div>

        {/* Recommended Contact */}
        <div className="oaa-card p-4" data-testid="recommended-contact-card">
          <h4 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-3">Recommended Contact</h4>
          {recommended_contact ? (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-[#0A0A0A]">{recommended_contact.FirstName} {recommended_contact.LastName}</p>
              <p className="text-xs text-neutral-500">{recommended_contact.JobTitle}</p>
              {recommended_contact.EmailID && recommended_contact.EmailID !== '-' && (
                <p className="flex items-center gap-1 text-xs text-[#F66922]"><Mail className="w-3 h-3" />{recommended_contact.EmailID}</p>
              )}
              <p className="text-xs text-neutral-400">{recommended_contact.Country}</p>
              {recommended_contact.linkedin && recommended_contact.linkedin !== '-' && (
                <a href={recommended_contact.linkedin} target="_blank" rel="noreferrer" className="text-[#F66922]"><Linkedin className="w-4 h-4" /></a>
              )}
            </div>
          ) : (
            <p className="text-xs text-neutral-300">No contact data available</p>
          )}
        </div>
      </div>

      {/* Charts + Map Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Engagements Chart */}
        <div className="oaa-card p-5" data-testid="engagements-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-[#0A0A0A]">Engagements</h3>
            <div className="flex gap-1">
              {[{ k: 'department', l: 'Department' }, { k: 'seniority', l: 'Seniority' }, { k: 'content', l: 'Content Type' }].map(v => (
                <button key={v.k} onClick={() => setChartView(v.k)}
                  className={`px-2.5 py-1 text-[10px] font-medium rounded-md transition-all ${chartView === v.k ? 'bg-[#F66922] text-white' : 'bg-neutral-50 text-neutral-500 hover:bg-neutral-100'}`}>
                  {v.l}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} width={120} />
              <Tooltip contentStyle={chartTooltipStyle} />
              <Bar dataKey="count" fill="#F66922" radius={[0, 4, 4, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Map */}
        <div className="oaa-card p-5" data-testid="signal-map">
          <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Signal Locations</h3>
          <div className="h-[300px] rounded-lg overflow-hidden">
            <MapContainer center={[20, 0]} zoom={2} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
              <TileLayer
                attribution="&copy; OpenStreetMap, &copy; CartoDB"
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              />
              {geo_markers.map((m, i) => (
                <CircleMarker
                  key={i}
                  center={[m.lat, m.lng]}
                  radius={Math.max(6, Math.min(m.count / 10, 30))}
                  pathOptions={{ fillColor: '#F66922', fillOpacity: 0.6, color: '#F66922', weight: 1 }}
                >
                  <Popup><span className="text-xs font-medium">{m.country}: {formatNum(m.count)} signals</span></Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </div>
      </div>

      {/* Recent Engagements */}
      <div className="oaa-card p-5" data-testid="recent-engagements">
        <h3 className="text-base font-semibold text-[#0A0A0A] mb-4">Recent Engagements</h3>
        <table className="data-table w-full">
          <thead>
            <tr>
              <th>Contact</th>
              <th>Job Title</th>
              <th>Content</th>
              <th>Signal Type</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {recent_engagements.slice(0, 10).map((e, i) => (
              <tr key={i}>
                <td className="font-medium">{e.FirstName} {e.LastName}</td>
                <td className="text-neutral-500 max-w-[200px] truncate">{e.JobTitle}</td>
                <td className="text-neutral-500 max-w-[200px] truncate">{e.AssetName}</td>
                <td><span className="bg-neutral-100 text-neutral-500 text-[10px] font-medium px-2 py-0.5 rounded">{e.signal_type}</span></td>
                <td className="text-neutral-400 text-xs">{e.CallDate?.split(' ')[0]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
